import React, { useMemo } from 'react';
import { Box, Chip, Divider, List, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { PullRequestDetails } from '@finvari/deliver';
import { PrWorkflow } from '../../../workflow/prWorkflow';
import { formatDate } from '../../../utils/deploymentUtils';
import { mergeStateHelp } from '../../../utils/mergeStatus';
import PrDescription from './PrDescription';

interface OverviewTabProps {
  pr: PullRequestDetails;
  workflow: PrWorkflow;
  openPrs: PullRequestDetails[];
  onSelectPr: (prNumber: number) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ pr, workflow, openPrs, onSelectPr }) => {
  const gh = pr.pr;

  // Stacked-PR navigation. "Below" = the upstream PR this one is stacked on (its base is that PR's
  // head); "above" = PRs stacked on top of this one (their base is this PR's head). Assumes the
  // one-at-a-time merge order the team uses today.
  const below = useMemo(
    () => openPrs.filter(p => p.pr.number !== gh.number && p.pr.head?.ref === gh.base?.ref),
    [openPrs, gh.number, gh.base?.ref]
  );
  const above = useMemo(
    () => openPrs.filter(p => p.pr.number !== gh.number && p.pr.base?.ref === gh.head?.ref),
    [openPrs, gh.number, gh.head?.ref]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Readiness checklist */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Readiness</Typography>
        {workflow.merged ? (
          <Chip color="success" size="small" label="Merged" />
        ) : workflow.ready ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="body2">Ready to merge — everything required is green.</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {workflow.blockers.map((b, i) => (
              <ListItem key={i} disableGutters sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <ErrorOutlineIcon color="warning" fontSize="small" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={b} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Divider />

      {/* Description */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>Description</Typography>
        {gh.body ? (
          <PrDescription body={gh.body} />
        ) : (
          <Typography variant="body2" color="text.secondary">No description provided.</Typography>
        )}
      </Box>

      {(below.length > 0 || above.length > 0) && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" gutterBottom>Stack</Typography>
            {above.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Above (merge after this):</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {above.map(p => (
                    <Chip key={p.pr.number} size="small" clickable label={`#${p.pr.number} ${p.pr.title}`}
                      onClick={() => onSelectPr(p.pr.number)} />
                  ))}
                </Box>
              </Box>
            )}
            {below.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">Below (merge before this):</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {below.map(p => (
                    <Chip key={p.pr.number} size="small" clickable label={`#${p.pr.number} ${p.pr.title}`}
                      onClick={() => onSelectPr(p.pr.number)} />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}

      <Divider />

      {/* Meta */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Created</Typography>
          <Typography variant="body2">{formatDate(gh.created_at)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Updated</Typography>
          <Typography variant="body2">{formatDate(gh.updated_at)}</Typography>
        </Box>
        {gh.mergeable_state && (
          <Box>
            <Typography variant="caption" color="text.secondary">Merge state</Typography>
            <Tooltip title={mergeStateHelp(gh.mergeable_state)}>
              <Typography variant="body2" sx={{ cursor: 'help' }}>{gh.mergeable_state}</Typography>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OverviewTab;
